import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Signal,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Observable, Subject } from 'rxjs';

export interface ScopeSelectDefault {
	id: number;
	name: string;
}
// Tornar MsSelectComponent genérico para aceitar qualquer tipo com id e name
export type WithIdName = { id?: number | any | null; name?: string };

@Component({
	selector: 'ms-select',
	templateUrl: './ms-select.component.html',
	styleUrls: ['./ms-select.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class MsSelectComponent<T extends WithIdName = WithIdName> implements OnInit, OnDestroy {
	@Input() formGroup: FormGroup;
	@Input() title: string;
	@Input() key: string;
	@Input() items: ScopeSelectDefault[] | null = null;
	@Input() fetch$: Observable<ScopeSelectDefault[]> | null = null;
	@Input() options: { userCanEdit?: boolean; userCanNew?: boolean } = {};
	@Output() changeHandle = new EventEmitter<any>();

	public scope: {
		loading: Signal<boolean>;
		userCanEdit: boolean;
		userCanNew: boolean;
	} = {
		loading: signal(false),
		userCanEdit: false,
		userCanNew: false,
	};

	public dynamicItems: ScopeSelectDefault[] = [];
	private _unsubscribeAll: Subject<any> = new Subject<any>();

	/**
	 * Constructor
	 */
	constructor(private _formBuilder: UntypedFormBuilder) {
		this.scope.loading = signal(false);
		if (!this.formGroup) {
			this.formGroup = this._formBuilder.group({
				name: [null, [Validators.required]],
			});
		}
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.scope.userCanEdit = !!this.options.userCanEdit;
		this.scope.userCanNew = !!this.options.userCanNew;
		// Normalize initial control value to an object { id, name? }
		this._setValue();
		this._findAll();
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	compareFn(a: T, b: T): boolean {
		return a && b && a.id === b.id;
	}

	createNew(select: NgSelectComponent) {
		select.close();
	}

	edit($event, data, select: NgSelectComponent) {
		select.close();
	}

	onChange(event: any) {
		// Garante que sempre será um objeto {id, name}
		const value = typeof event === 'object' ? event : { id: event };
		this.formGroup.get(this.key)?.setValue(value);
		this.changeHandle.emit(value);
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Private methods
	// -----------------------------------------------------------------------------------------------------
	private _findAll() {
		if (this.fetch$) {
			this.scope.loading = signal(true);
			this.fetch$.pipe().subscribe({
				next: (data) => {
					this.dynamicItems = data;
					this.scope.loading = signal(false);
				},
				error: () => (this.scope.loading = signal(false)),
			});
		} else {
			this.dynamicItems = this.items ?? [];
		}
	}

	private _setValue() {
		const ctrl = this.formGroup?.get(this.key);
		if (!ctrl) return;
		const value = ctrl.value;
		if (value !== '' && value !== null && value !== undefined && typeof value !== 'object') {
			ctrl.setValue({ id: value });
		}
	}
}
